import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  ENTRY_STATUS, money, shortDate,
  type Bonspiel, type BonspielEntry, type EntryStatus,
} from '@lcc/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  Card, CheckField, Empty, ErrorNote, Loading, Modal, Pill, Status, Tabs, TextField,
  errorMessage, useToast,
} from '../components/ui';
import { PageHeader } from '../App';

interface BracketGame {
  id: string; bracketKey: string; eventCode: string | null; eventName: string | null;
  round: number; position: number; sheetNumber: number | null;
  homeTeamName: string | null; awayTeamName: string | null;
  homeEntryId: string | null; awayEntryId: string | null;
  homeScore: number | null; awayScore: number | null;
  status: string; winnerEntryId: string | null;
  winnerToKey: string | null; loserToKey: string | null;
}

export function BonspielDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const [tab, setTab] = useState<'entries' | 'bracket'>('entries');
  const [drawing, setDrawing] = useState(false);
  const [scoring, setScoring] = useState<BracketGame | null>(null);

  const spiel = useQuery({
    queryKey: ['bonspiel', id],
    queryFn: () => api.get<{ data: Bonspiel }>(`/bonspiels/${id}`).then((r) => r.data),
  });
  const entries = useQuery({
    queryKey: ['entries', id],
    queryFn: () => api.get<{ data: BonspielEntry[] }>(`/bonspiels/${id}/entries`).then((r) => r.data),
  });
  const bracket = useQuery({
    queryKey: ['bracket', id],
    queryFn: () => api.get<{ data: BracketGame[] }>(`/bonspiels/${id}/bracket`).then((r) => r.data),
  });

  if (spiel.isLoading) return <><PageHeader title="Bonspiel" /><div className="content"><Loading /></div></>;
  if (spiel.error) return <><PageHeader title="Bonspiel" /><div className="content"><ErrorNote error={spiel.error} /></div></>;

  const s = spiel.data!;
  const confirmed = entries.data?.filter((e) => e.status === 'confirmed') ?? [];
  const unpaid = confirmed.filter((e) => !e.isPaid);
  const byEvent = new Map<string, BracketGame[]>();
  for (const g of bracket.data ?? []) {
    const key = g.eventCode ?? '?';
    if (!byEvent.has(key)) byEvent.set(key, []);
    byEvent.get(key)!.push(g);
  }

  return (
    <>
      <PageHeader
        title={s.name}
        sub={<>
          <Link to="/bonspiels">Bonspiels</Link> ·{' '}
          {s.startsOn === s.endsOn ? shortDate(s.startsOn)
            : `${shortDate(s.startsOn)} – ${shortDate(s.endsOn)}`}
          {' '}· {s.kind === 'open' ? 'open' : 'in-house'}
          {s.entryFeeCents ? ` · ${money(s.entryFeeCents)} entry` : ''}
        </>}
        actions={can('convenor') && (
          <button className="btn btn--accent" type="button" onClick={() => setDrawing(true)}>
            {bracket.data?.length ? 'Redraw bracket' : 'Draw the bracket'}
          </button>
        )}
      />

      <div className="content">
        {unpaid.length > 0 && (
          <div className="banner banner--warn" style={{ marginBottom: '1rem' }}>
            <strong>{unpaid.length} confirmed team{unpaid.length === 1 ? '' : 's'} have not paid.</strong>{' '}
            {unpaid.map((e) => e.teamName).join(', ')}
          </div>
        )}

        <Tabs
          active={tab} onChange={setTab}
          tabs={[
            { id: 'entries', label: 'Entries', count: entries.data?.length },
            { id: 'bracket', label: 'Bracket', count: bracket.data?.length },
          ]}
        />

        {tab === 'entries' && (
          entries.isLoading ? <Loading /> :
          !entries.data?.length
            ? <Card><Empty title="No entries yet">
                Entries arrive from the form on the website, or add them by hand at the club.
              </Empty></Card>
            : (
              <Card flush>
                <div className="table-wrap">
                  <table className="t">
                    <thead>
                      <tr>
                        <th>Team</th><th>Skip</th><th>Club</th><th>Contact</th>
                        <th>Players</th><th>Status</th><th>Paid</th>
                        {can('convenor') && <th />}
                      </tr>
                    </thead>
                    <tbody>
                      {entries.data.map((e) => (
                        <EntryRow key={e.id} spielId={id} entry={e} canEdit={can('convenor')} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )
        )}

        {tab === 'bracket' && (
          bracket.isLoading ? <Loading /> :
          !bracket.data?.length
            ? <Card><Empty title="No bracket drawn">
                Confirm the entries, then draw the bracket. Event A is a knock-out over every
                team; teams knocked out of each round drop into the next event down the ladder.
              </Empty></Card>
            : (
              <div className="stack">
                {[...byEvent.entries()].map(([event, games]) => {
                  const rounds = new Map<number, BracketGame[]>();
                  for (const g of games) {
                    if (!rounds.has(g.round)) rounds.set(g.round, []);
                    rounds.get(g.round)!.push(g);
                  }
                  return (
                    <Card key={event} title={`${event} Event`}>
                      <div className="bracket">
                        {[...rounds.entries()].sort((a, b) => a[0] - b[0]).map(([round, rgames]) => (
                          <div className="bracket__round" key={round}>
                            <h4>{roundName(round, rounds.size)}</h4>
                            {rgames.sort((a, b) => a.position - b.position).map((g) => (
                              <BracketCard key={g.id} game={g}
                                onScore={can('convenor') ? () => setScoring(g) : undefined} />
                            ))}
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )
        )}
      </div>

      {drawing && <DrawBracketModal spiel={s} confirmed={confirmed.length} onClose={() => setDrawing(false)} />}
      {scoring && <BracketScoreModal spielId={id} game={scoring} onClose={() => setScoring(null)} />}
    </>
  );
}

function roundName(round: number, total: number): string {
  const fromEnd = total - round;
  if (fromEnd === 0) return 'Final';
  if (fromEnd === 1) return 'Semi-finals';
  if (fromEnd === 2) return 'Quarter-finals';
  return `Round ${round}`;
}

function BracketCard({ game, onScore }: { game: BracketGame; onScore?: () => void }) {
  const done = game.status === 'final' || game.status === 'forfeit';
  const side = (name: string | null, score: number | null, entryId: string | null) => (
    <div className={done && game.winnerEntryId === entryId ? 'bg-game__side win' : 'bg-game__side'}>
      {name ? <span>{name}</span> : <span className="tbd">to be decided</span>}
      {score !== null && <span className="s">{score}</span>}
    </div>
  );
  const ready = game.homeEntryId && game.awayEntryId;

  return (
    <div className="bg-game">
      {side(game.homeTeamName, game.homeScore, game.homeEntryId)}
      {side(game.awayTeamName, game.awayScore, game.awayEntryId)}
      <div className="bg-game__foot">
        <span className="mono">{game.bracketKey}</span>
        {game.loserToKey && <span title="Loser drops to">↘ {game.loserToKey}</span>}
        {onScore && ready && !done && (
          <button className="btn btn--sm" type="button" onClick={onScore}
            style={{ marginLeft: 'auto', padding: '.1rem .45rem', fontSize: '.7rem' }}>
            Score
          </button>
        )}
      </div>
    </div>
  );
}

function EntryRow(
  { spielId, entry, canEdit }: { spielId: string; entry: BonspielEntry; canEdit: boolean },
) {
  const qc = useQueryClient();
  const toast = useToast();
  const update = useMutation({
    mutationFn: (patch: Partial<{ status: EntryStatus; isPaid: boolean }>) =>
      api.patch(`/bonspiels/${spielId}/entries/${entry.id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entries', spielId] });
      void qc.invalidateQueries({ queryKey: ['bonspiel', spielId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <tr>
      <td><b>{entry.teamName}</b></td>
      <td>{entry.skipName}</td>
      <td className="muted small">{entry.homeClub ?? '—'}</td>
      <td className="muted small">
        {entry.contactEmail ?? '—'}<br />{entry.contactPhone ?? ''}
      </td>
      <td className="muted small">{entry.players.map((p) => p.name).join(', ') || '—'}</td>
      <td>
        {canEdit ? (
          <select
            value={entry.status} disabled={update.isPending}
            onChange={(e) => update.mutate({ status: e.target.value as EntryStatus })}
            style={{ fontSize: '.8rem', padding: '.2rem .35rem' }}
          >
            {ENTRY_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : <Status value={entry.status} />}
      </td>
      <td>
        {canEdit ? (
          <button
            className={entry.isPaid ? 'btn btn--sm' : 'btn btn--sm btn--danger'}
            type="button" disabled={update.isPending}
            onClick={() => update.mutate({ isPaid: !entry.isPaid })}
          >
            {entry.isPaid ? 'Paid' : 'Unpaid'}
          </button>
        ) : (entry.isPaid ? <Pill tone="good">paid</Pill> : <Pill tone="warn">unpaid</Pill>)}
      </td>
      {canEdit && <td />}
    </tr>
  );
}

function DrawBracketModal(
  { spiel, confirmed, onClose }: { spiel: Bonspiel; confirmed: number; onClose: () => void },
) {
  const qc = useQueryClient();
  const toast = useToast();
  const [codes, setCodes] = useState(
    spiel.events.length ? spiel.events.map((e) => e.code).join(',') : 'A,B,C,D',
  );
  const [random, setRandom] = useState(false);
  const [preview, setPreview] = useState<{ summary?: { games: number; entries: number } } | null>(null);

  const run = useMutation({
    mutationFn: (dryRun: boolean) => api.post<{ summary?: { games: number; entries: number }; games?: number }>(
      `/bonspiels/${spiel.id}/bracket`,
      {
        eventCodes: codes.split(',').map((c) => c.trim()).filter(Boolean),
        seedBy: random ? 'random' : 'entry_order',
        dryRun,
      },
    ),
    onSuccess: (r, dryRun) => {
      if (dryRun) { setPreview(r); return; }
      void qc.invalidateQueries({ queryKey: ['bracket', spiel.id] });
      toast.success(`Bracket drawn — ${r.games} games.`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <Modal
      title="Draw the bracket"
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn" type="button" disabled={run.isPending}
            onClick={() => run.mutate(true)}>Preview</button>
          <button className="btn btn--accent" type="button"
            disabled={run.isPending || confirmed < 2}
            onClick={() => run.mutate(false)}>Draw</button>
        </>
      }
    >
      <div className="stack">
        {run.error ? <ErrorNote error={run.error} /> : null}
        <div className="banner banner--info">
          <strong>{confirmed} confirmed entr{confirmed === 1 ? 'y' : 'ies'}</strong> will be drawn.
          Only confirmed entries are included.
        </div>
        <fieldset disabled={run.isPending}>
          <TextField label="Events" value={codes} onChange={setCodes}
            hint="Comma separated. Event A is a knock-out over every team; losers of A round 1 play B, losers of round 2 play C, and so on." />
          <CheckField label="Draw at random" checked={random} onChange={setRandom}
            hint="Otherwise teams are seeded in the order they entered." />
        </fieldset>
        {preview?.summary && (
          <div className="banner banner--good">
            {preview.summary.games} games across those events for {preview.summary.entries} teams.
            Nothing saved yet.
          </div>
        )}
      </div>
    </Modal>
  );
}

function BracketScoreModal(
  { spielId, game, onClose }: { spielId: string; game: BracketGame; onClose: () => void },
) {
  const qc = useQueryClient();
  const toast = useToast();
  const [home, setHome] = useState('');
  const [away, setAway] = useState('');

  const save = useMutation({
    mutationFn: () => api.put(`/bonspiels/${spielId}/bracket/${game.id}/score`,
      { homeScore: Number(home), awayScore: Number(away) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bracket', spielId] });
      toast.success('Result recorded — teams advanced.');
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const tied = home !== '' && away !== '' && Number(home) === Number(away);

  return (
    <Modal
      title={`${game.eventCode} Event — ${game.bracketKey}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn btn--accent" type="button"
            disabled={save.isPending || home === '' || away === '' || tied}
            onClick={() => save.mutate()}>Save result</button>
        </>
      }
    >
      <div className="stack">
        {save.error ? <ErrorNote error={save.error} /> : null}
        <div className="grid grid--2">
          <TextField label={game.homeTeamName ?? 'Home'} type="number" value={home} onChange={setHome} />
          <TextField label={game.awayTeamName ?? 'Away'} type="number" value={away} onChange={setAway} />
        </div>
        {tied && (
          <div className="banner banner--warn">
            A knock-out game cannot be tied — play an extra end.
          </div>
        )}
        <p className="small muted">
          Saving advances the winner
          {game.winnerToKey ? ` to ${game.winnerToKey}` : ' (this is the event final)'}
          {game.loserToKey && ` and drops the loser to ${game.loserToKey}`}.
        </p>
      </div>
    </Modal>
  );
}
